import { FaceDetection } from '@vladmandic/face-api';
import { useEffect, useState } from 'react';

import { FaceApiClient } from 'lib/clients/FaceApiClient';
import FrameTriggerClient from 'lib/clients/FrameTriggerClient';
import LivePhotoCaptureClient from 'lib/clients/LivePhotoCaptureClient';
import useInquiryFeatureFlag from 'lib/hooks/useInquiryFeatureFlag';
import { CameraInspectionData } from 'verify/lib/interfaces/cameraInspection';
import { calculateGlareRatio } from 'verify/lib/utils/image';
import { bufferHorizontalImage } from 'verify/lib/utils/webcam';
import { CaptureMethod, ClientExtraction } from 'verify/models/verification';
import BaseScanner from 'verify/scanners/BaseScanner';
import {
  DETECT_INTERVAL,
  GOVERNMENT_ID_TRIGGER_CONFIGS,
  GovernmentIdTriggerParams,
  GovernmentIdTriggerType,
} from 'verify/scanners/GovernmentIdScanner/client';

const FACE_DETECTION_THRESHOLD = 0.3;

interface GovernmentIdScannerProps {
  delayUntilManualCaptureMs?: number;
  handleCapture: (
    imageDataUris: string[],
    captureMethod: CaptureMethod,
    cameraInspectionData: CameraInspectionData,
    clientExtractions?: ClientExtraction[],
  ) => Promise<void>;
  handleError: (err: Error | string, state: 'load' | 'capture') => void;
  isPassport: boolean;
  labelTakePhoto: string;
  overlay: JSX.Element;
  videoConstraints: MediaTrackConstraints;
  zoomFactor: number;
}

const GovernmentIdScanner = ({
  /**
   * Manual capture was always enabled for GovernmentIdScanner before this prop was implemented.
   */
  delayUntilManualCaptureMs = 0,
  handleCapture,
  handleError,
  isPassport,
  labelTakePhoto,
  overlay,
  videoConstraints,
  zoomFactor,
}: GovernmentIdScannerProps) => {
  // If errored, stops attempting face detection / autocapture entirely.
  const [errored, setErrored] = useState(false);

  const disableGovIdFaceApi = useInquiryFeatureFlag('inquiry:disable-gov-id-face-api');

  useEffect(() => {
    if (disableGovIdFaceApi) return;

    try {
      FaceApiClient.loadModels();
    } catch {
      setErrored(true);
    }
  }, []);

  const paramsFromVideoFrame = async (
    videoElement: HTMLVideoElement,
    canvasBuffer: HTMLCanvasElement,
    _captureClient: LivePhotoCaptureClient,
    triggerClient: FrameTriggerClient<GovernmentIdTriggerType, GovernmentIdTriggerParams>,
  ): Promise<GovernmentIdTriggerParams> => {
    bufferHorizontalImage({ source: videoElement, precreatedCanvas: canvasBuffer, zoomFactor });

    let detection: FaceDetection[] = [];
    if (!errored && FaceApiClient.isLoaded() && FaceApiClient.isMediaLoaded(videoElement)) {
      try {
        detection = await FaceApiClient.detectFaces(canvasBuffer, {
          scoreThreshold: FACE_DETECTION_THRESHOLD,
        });
      } catch (err) {
        // NOTE: Some Android devices fail to compile the fragment shader.
        // If many devices hit this, consider following recommendation here:
        // https://github.com/tensorflow/tfjs/issues/952

        // Here, we stop tick'ing but do nothing else because they can manually take a photo.
        handleError(err as any, 'capture');
        setErrored(true);
      }
    }

    let faceBoundingBox;
    const canvasWidth = canvasBuffer.width;
    const canvasHeight = canvasBuffer.height;
    if (detection.length === 1 && canvasWidth > 0 && canvasHeight > 0) {
      // Normalize bounding box
      faceBoundingBox = detection[0].box.rescale({
        width: 1 / canvasWidth,
        height: 1 / canvasHeight,
      });
    }

    return {
      currentTrigger: triggerClient.getTriggered().getType(),
      faceBoundingBox: faceBoundingBox ?? null,
      glareRatio: calculateGlareRatio(canvasBuffer),
      isPassport: isPassport,
    };
  };
  const shouldSubmit = (
    _params: GovernmentIdTriggerParams,
    triggerClient: FrameTriggerClient<GovernmentIdTriggerType, GovernmentIdTriggerParams>,
  ) => {
    return triggerClient.getTriggered().getType() === GovernmentIdTriggerType.Capture;
  };

  return (
    <BaseScanner<GovernmentIdTriggerType, GovernmentIdTriggerParams>
      handleCapture={({ imageSrcs, captureMethod, cameraInspectionData }) =>
        handleCapture(imageSrcs, captureMethod, cameraInspectionData)
      }
      handleFailed={handleError}
      detectInterval={DETECT_INTERVAL}
      labelTakePhoto={labelTakePhoto}
      videoConstraints={videoConstraints}
      triggerConfigs={GOVERNMENT_ID_TRIGGER_CONFIGS}
      paramsFromVideoFrame={paramsFromVideoFrame}
      shouldSubmit={shouldSubmit}
      overlay={overlay}
      delayUntilManualCaptureMs={delayUntilManualCaptureMs}
      scannerType='governmentId'
      zoomFactor={zoomFactor}
    />
  );
};

export default GovernmentIdScanner;
